import React, { createContext, useReducer } from 'react';

/**
 * 建立 Context，預設值裡放一個空的 state 與空的 dispatch
 */
export const NotificationContext = createContext({
  state: { notifications: [] },
  dispatch: () => {
    console.warn('未初始化 NotificationContext Provider');
  },
});

/**
 * 初始狀態：notifications 用來存「所有公司合併後」的通知資料
 * 每個項目的結構預期如下：
 * {
 *    customer: <公司識別>,
 *    notifications: {
 *       observe: [ ... ],
 *       alert: [ ... ],
 *       danger: [ ... ],
 *       shutdown: [ ... ],
 *       overheat: [ ... ],
 *       offline: [ ... ]
 *    },
 *    updatedAt: <最後更新時間>
 * }
 */
const initialState = {
  notifications: [],
};

// 輔助函式：補上缺少的區域，確保每個 notifications 物件都有完整 6 個區域
function fillMissingRegions(notifs, validRegions) {
  const filled = { ...notifs };
  validRegions.forEach((region) => {
    if (!filled.hasOwnProperty(region)) {
      filled[region] = [];
    }
  });
  return filled;
}

/**
 * Reducer 處理不同類型的通知
 */
function notificationReducer(state, action) {
  const validRegions = ['observe', 'alert', 'danger', 'shutdown', 'overheat', 'offline'];
  switch (action.type) {
    case 'INITIAL_NOTIFICATIONS': {
      // 假設 payload 內部資料已完整，也可依需求先補齊各區
      return {
        ...state,
        notifications: action.payload.map((company) => ({
          customer: company.customer,
          notifications: fillMissingRegions(company.notifications, validRegions),
          updatedAt: company.updatedAt
        })),
      };
    }

    case 'UPDATE_NOTIFICATIONS': {
      // 先把 payload 裡的資料存入 Map，key = company.customer，value = 整個公司資料（包含 notifications 與 updatedAt）
      const newNotificationsMap = new Map();
      action.payload.forEach((company) => {
        // 補上缺失的區域
        company.notifications = fillMissingRegions(company.notifications, validRegions);
        newNotificationsMap.set(company.customer, company);
      });

      let updatedNotifications = [];

      // 遍歷舊 state 裡的資料
      state.notifications.forEach((oldCompany) => {
        const { customer, notifications, updatedAt } = oldCompany;
        const newCompany = newNotificationsMap.get(customer);

        if (newCompany) {
          const mergedNotifs = {};
          // 對每個區域合併新舊資料
          validRegions.forEach((region) => {
            const oldArray = Array.isArray(notifications[region]) ? notifications[region] : [];
            const newArray = Array.isArray(newCompany.notifications[region])
              ? newCompany.notifications[region]
              : [];
            const combined = [...oldArray, ...newArray];
            // 依 _id 去重，若重複則以 updatedAt 較新的為主
            const uniqueMap = new Map();
            combined.forEach((notif) => {
              const id = notif._id;
              const exist = uniqueMap.get(id);
              if (!exist || new Date(exist.updatedAt) < new Date(notif.updatedAt)) {
                uniqueMap.set(id, notif);
              }
            });
            // 轉陣列後依 currentRegions.timestamp 降序排序
            const mergedArray = Array.from(uniqueMap.values()).sort(
              (a, b) => new Date(b.currentRegions?.timestamp || 0) - new Date(a.currentRegions?.timestamp || 0)
            );
            mergedNotifs[region] = mergedArray;
          });
          updatedNotifications.push({
            customer,
            notifications: mergedNotifs,
            updatedAt: newCompany.updatedAt, // 使用 payload 中的新 updatedAt
          });
          // 移除已處理的公司
          newNotificationsMap.delete(customer);
        } else {
          updatedNotifications.push(oldCompany);
        }
      });

      // 若 payload 中有全新公司（舊 state 中找不到），直接加入，並補上缺失區域
      newNotificationsMap.forEach((company, customer) => {
        let mergedNotifs = {};
        validRegions.forEach((region) => {
          const arr = Array.isArray(company.notifications[region])
            ? company.notifications[region]
            : [];
          const uniqueMap = new Map();
          arr.forEach((notif) => {
            uniqueMap.set(notif._id, notif);
          });
          const sortedArr = Array.from(uniqueMap.values()).sort(
            (a, b) => new Date(b.currentRegions?.timestamp || 0) - new Date(a.currentRegions?.timestamp || 0)
          );
          mergedNotifs[region] = sortedArr;
        });
        updatedNotifications.push({
          customer,
          notifications: mergedNotifs,
          updatedAt: company.updatedAt,
        });
      });

      return {
        ...state,
        notifications: updatedNotifications,
      };
    }

    case 'MORE_NOTIFICATIONS': {
      // MORE_NOTIFICATIONS 的 payload 預期格式：
      // 每個 item 為 { customer, notifications: { [region]: [...] }, updatedAt }
      // 將 state 裡的資料先轉為 Map（key = customer）
      const existingMap = new Map();
      state.notifications.forEach((item) => {
        // 確保原有資料的 notifications 也完整
        existingMap.set(item.customer, {
          ...item,
          notifications: fillMissingRegions(item.notifications, validRegions),
        });
      });

      const item = action.payload;
      const { customer, notifications: newNotifObj, updatedAt } = item;
      if (existingMap.has(customer)) {
        const existingItem = existingMap.get(customer);
        const mergedNotifs = { ...existingItem.notifications };

        // 處理 payload 中所有區域的通知資料（可能只有部分區域有資料）
        Object.keys(newNotifObj).forEach((region) => {
          const oldArray = Array.isArray(mergedNotifs[region]) ? mergedNotifs[region] : [];
          const newArray = Array.isArray(newNotifObj[region]) ? newNotifObj[region] : [];
          const combined = [...oldArray, ...newArray];
          const uniqueMap = new Map();

          combined.forEach((notif) => {
            const id = notif._id;
            const exist = uniqueMap.get(id);
            if (!exist || new Date(exist.updatedAt) < new Date(notif.updatedAt)) {
              uniqueMap.set(id, notif);
            }
          });

          const mergedArray = Array.from(uniqueMap.values()).sort(
            (a, b) => new Date(b.currentRegions?.timestamp || 0) - new Date(a.currentRegions?.timestamp || 0)
          );
          mergedNotifs[region] = mergedArray;
        });

        // 使用 payload 中的新 updatedAt（若未提供則保留原來的）
        existingMap.set(customer, {
          ...existingItem,
          notifications: mergedNotifs,
          updatedAt: updatedAt || existingItem.updatedAt,
        });
      } else {
        // 若是全新公司的資料，直接加入前先補上缺失區域
        existingMap.set(customer, {
          ...item,
          notifications: fillMissingRegions(item.notifications, validRegions),
        });
      }

      const updatedNotifications = Array.from(existingMap.values());
      return {
        ...state,
        notifications: updatedNotifications,
      };
    }

    default:
      // console.log('Reducer received action:', action);
      return state;
  }
}

/**
 * NotificationProvider：負責存放通知資料
 */
export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);

  return (
    <NotificationContext.Provider value={{ state, dispatch }}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;
